import { useMemo } from 'react'
import { useFormState } from 'react-hook-form'

import { useFormWithZod } from '#/shared/lib/form'
import { EMPTY_LOCATION_DEFAULTS, useLocationSchema } from './location-schema'
import type { LocationFormData } from './location-schema'

interface UseLocationFormParams {
  defaultValues?: Partial<LocationFormData>
  onSubmit?: (data: LocationFormData) => void | Promise<void>
}

export function useLocationForm({
  defaultValues,
  onSubmit,
}: UseLocationFormParams = {}) {
  const schema = useLocationSchema()
  const values = useMemo<LocationFormData>(
    () => ({
      ...EMPTY_LOCATION_DEFAULTS,
      ...defaultValues,
      origin: { ...EMPTY_LOCATION_DEFAULTS.origin, ...defaultValues?.origin },
      destination: {
        ...EMPTY_LOCATION_DEFAULTS.destination,
        ...defaultValues?.destination,
      },
    }),
    [defaultValues],
  )
  const form = useFormWithZod(schema, {
    defaultValues: values,
    values,
    resetOptions: { keepDirtyValues: true },
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { errors, dirtyFields } = useFormState({ control: form.control })
  const isDirty = Object.keys(dirtyFields).length > 0

  return {
    control: form.control,
    register: form.register,
    setValue: form.setValue,
    getValues: form.getValues,
    trigger: form.trigger,
    clearErrors: form.clearErrors,
    errors,
    isSubmitting: form.isSubmitting,
    isDirty,
    handleSubmit: onSubmit ? form.handleSubmit(onSubmit) : undefined,
    reset: form.reset,
  }
}
